/**
 * App shell — rail, header, scroll container, screens, upgrade drawer.
 *
 * Local UI state only, exactly the shape the handoff specifies. Everything else
 * is server data. Screen and call id are mirrored into the URL hash so a call can
 * be linked to and a reload lands back on the same screen.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Rail } from './components/Rail';
import { Header } from './components/Header';
import { UpgradeDrawer } from './components/UpgradeDrawer';
import { AskVoiceProp } from './components/AskVoiceProp';
import { CrmActivationModal } from './components/CrmActivationModal';
import { Overview } from './screens/Overview';
import { Calls } from './screens/Calls';
import { CallDetail } from './screens/CallDetail';
import { Themes } from './screens/Themes';
import { VoiceStudio } from './screens/VoiceStudio';
import { Team } from './screens/Team';
import { Failure, LoadingPanel } from './components/states';
import { NoAccess } from './components/NoAccess';
import { api, ApiError } from './api/client';
import { c } from './theme/tokens';
import type { Screen, UpgradeFocus, UpgradeTier } from './types';
import { Lockup } from './components/Lockup';
import type {
  AccessDenial,
  BranchSummary,
  CallDetail as Detail,
  CallFilter,
  CallsResult,
  DashboardContext,
  RangeKey,
  SentimentResult,
  SummaryResult,
  ThemesResult,
} from './api/types';

const RANGE_KEYS: RangeKey[] = ['today', 'yesterday', '7d', '30d', '90d'];

function CustomerPicker({
  tenants,
  onSelect,
}: {
  tenants: { id: string; name: string; slug: string }[];
  onSelect: (tenantId: string) => void;
}) {
  return (
    <main style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 28, background: c.surface }}>
      <section style={{ width: 'min(680px, 100%)', display: 'flex', flexDirection: 'column', gap: 18 }}>
        <Lockup />
        <div>
          <div style={{ color: c.ink3, fontSize: 11, letterSpacing: '0.13em', fontWeight: 700 }}>VOICEPROP · INTERNAL</div>
          <h1 style={{ margin: '8px 0 0', fontSize: 32, color: c.ink }}>Choose a customer</h1>
          <p style={{ margin: '8px 0 0', color: c.ink2, lineHeight: 1.55 }}>Customer information is kept separate. Choose the business you need to work on.</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          {tenants.map(tenant => (
            <button key={tenant.id} onClick={() => onSelect(tenant.id)} className="vp-reset vp-outline" style={{ textAlign: 'left', padding: '20px', border: '1px solid #d8dee7', borderRadius: 12, background: '#fff', cursor: 'pointer' }}>
              <strong style={{ display: 'block', color: c.ink, fontSize: 16 }}>{tenant.name}</strong>
              <span style={{ display: 'block', marginTop: 6, color: c.ink3, fontSize: 12 }}>Open Action Centre →</span>
            </button>
          ))}
        </div>
      </section>
    </main>
  );
}

function isRangeKey(value: string | null | undefined): value is RangeKey {
  return !!value && (RANGE_KEYS as string[]).includes(value);
}

interface HashState {
  screen: Screen;
  callId: string | null;
  range: RangeKey;
}

/**
 * The range lives in the URL alongside the screen. A dashboard whose numbers
 * depend on a window that is invisible to the address bar cannot be shared,
 * bookmarked or reported against — two people looking at "the dashboard" would
 * be looking at different data with no way to tell.
 */
function readHash(): HashState {
  const [path, query] = window.location.hash.replace(/^#\/?/, '').split('?');
  const [screen, id] = (path ?? '').split('/');
  const rawRange = new URLSearchParams(query ?? '').get('range');
  // The Action Centre is a service record, not a blank daily dashboard. Open
  // on the last 30 days unless a user explicitly chooses another range.
  const range: RangeKey = isRangeKey(rawRange) ? rawRange : '30d';

  const valid: Screen[] = ['overview', 'calls', 'detail', 'themes', 'studio', 'team'];
  if (screen === 'detail' && id) return { screen: 'detail', callId: id, range };
  if ((valid as string[]).includes(screen ?? '')) return { screen: screen as Screen, callId: null, range };
  return { screen: 'overview', callId: null, range };
}

function hashFor(screen: Screen, callId: string | null, range: RangeKey): string {
  const path = screen === 'detail' && callId ? `#/detail/${callId}` : `#/${screen}`;
  // Keep every explicit range in the URL. With 30d as the default, omitting
  // `today` would make a copied/pasted today view silently reopen as 30 days.
  return `${path}?range=${range}`;
}

/**
 * Navigation pushes, so the browser's back button steps back through screens
 * rather than leaving the dashboard entirely.
 */
function writeHash(screen: Screen, callId: string | null, range: RangeKey) {
  const next = hashFor(screen, callId, range);
  if (window.location.hash !== next) window.history.pushState(null, '', next);
}

export function App() {
  const initial = useMemo(readHash, []);

  const [screen, setScreen] = useState<Screen>(initial.screen);
  const [callId, setCallId] = useState<string | null>(initial.callId);
  const [range, setRange] = useState<RangeKey>(initial.range);
  const [filter, setFilter] = useState<CallFilter>('All');
  const [callDate, setCallDate] = useState<string | null>(null);
  const [topic, setTopic] = useState<string | null>(null);
  const [upgrade, setUpgrade] = useState<UpgradeTier>(null);
  const [upgradeFocus, setUpgradeFocus] = useState<UpgradeFocus | undefined>();
  const [crmActivationOpen, setCrmActivationOpen] = useState(false);

  const openUpgrade = (tier: Exclude<UpgradeTier, null>, focus?: UpgradeFocus) => {
    // Shaun already has Pro. Advise is deliberately a persistent activation
    // reminder until CRM property data is linked, not a second Pro upsell.
    if (tier === 'pro' && ctx?.tenant.tier === 'pro') {
      setCrmActivationOpen(true);
      return;
    }
    setUpgradeFocus(focus);
    setUpgrade(tier);
  };

  const closeUpgrade = () => {
    setUpgrade(null);
    setUpgradeFocus(undefined);
  };

  const [branchId, setBranchId] = useState<string | undefined>(undefined);
  // Honoured only for an explicit ModelProp internal grant on the server.
  const [tenantId, setTenantId] = useState<string | undefined>(undefined);
  const [ctx, setCtx] = useState<DashboardContext | null>(null);
  const [ctxError, setCtxError] = useState<string | null>(null);
  /*
   * An access refusal is held separately from a load failure because they are
   * different events needing opposite screens. Collapsing them is what put a
   * "Try again" button under a permission decision and headed it "CANNOT REACH
   * THE DASHBOARD API" — blaming the network for a deliberate, correct answer.
   */
  const [accessDenied, setAccessDenied] = useState<{ denial: AccessDenial | null; message: string } | null>(null);
  const contextRequest = useRef(0);

  const [summary, setSummary] = useState<SummaryResult | null>(null);
  const [, setSummaryLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const [calls, setCalls] = useState<CallsResult | null>(null);
  const [callsLoading, setCallsLoading] = useState(false);
  const [callsError, setCallsError] = useState<string | null>(null);

  const [detail, setDetail] = useState<Detail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  // Bumped to force a refetch; setting state to its own value would not.
  const [reloadDetail, setReloadDetail] = useState(0);
  const [reloadThemes, setReloadThemes] = useState(0);

  const [themes, setThemes] = useState<ThemesResult | null>(null);
  const [sentiment, setSentiment] = useState<SentimentResult | null>(null);
  const [themesLoading, setThemesLoading] = useState(false);
  const [themesError, setThemesError] = useState<string | null>(null);

  useEffect(() => {
    writeHash(screen, callId, range);
  }, [screen, callId, range]);

  // A pasted #/team link from someone without team rights would otherwise render
  // a blank pane and fire requests the server refuses. Send them to Overview.
  useEffect(() => {
    if (screen === 'team' && ctx && !ctx.actor?.canManageTeam) setScreen('overview');
  }, [screen, ctx]);

  // Follow the address bar: back/forward and a pasted deep link all have to land
  // on the right screen, not silently leave the app showing the previous one.
  useEffect(() => {
    const sync = () => {
      const next = readHash();
      setScreen(next.screen);
      setRange(next.range);
      setCallId(prev => {
        if (next.callId && next.callId !== prev) setDetail(null);
        return next.callId;
      });
    };
    window.addEventListener('hashchange', sync);
    window.addEventListener('popstate', sync);
    return () => {
      window.removeEventListener('hashchange', sync);
      window.removeEventListener('popstate', sync);
    };
  }, []);

  const message = (e: unknown) => (e instanceof ApiError ? e.message : 'Something went wrong loading this.');

  // Context
  const loadCtx = useCallback(() => {
    const requestId = ++contextRequest.current;
    // Never render customer A while customer B is loading.
    setCtx(null);
    setCtxError(null);
    setAccessDenied(null);
    api
      .context(branchId, tenantId)
      .then(next => {
        if (contextRequest.current === requestId) setCtx(next);
      })
      .catch(e => {
        if (contextRequest.current !== requestId) return;
        if (e instanceof ApiError && e.isAccessDenied) {
          setAccessDenied({ denial: e.access ?? null, message: e.message });
          return;
        }
        setCtxError(message(e));
      });
  }, [branchId, tenantId]);

  useEffect(loadCtx, [loadCtx]);

  // Summary — needed by the overview and by the rail's counts.
  const loadSummary = useCallback(() => {
    // An internal user starts at the customer picker. Do not fetch Shaun (or
    // anyone else) behind that picker until they explicitly select them.
    if (!ctx || (ctx.actor?.internal && !tenantId)) return;
    setSummaryLoading(true);
    api
      .summary(ctx.branch.id, range)
      .then(next => {
        setSummary(next);
        setLastUpdated(new Date());
      })
      .catch(() => setSummary(null))
      .finally(() => setSummaryLoading(false));
  }, [ctx, range]);

  useEffect(loadSummary, [loadSummary]);

  // Calls
  const loadCalls = useCallback(
    (cursor?: string | null) => {
      if (!ctx) return;
      setCallsLoading(true);
      setCallsError(null);
      // Base tenants are message archives. Force the filter in state/load even
      // for deep links or stale browser state, not merely by hiding chips.
      const effectiveFilter = ctx.capabilities.cap_review_queue ? filter : 'Messages';
      api
        // Call Record is the archive, not a mirror of the Overview's date
        // picker. Without an explicit chart-day drill-down, its API defaults to
        // the last 30 days so "Every call" never silently means "today".
        .calls(ctx.branch.id, effectiveFilter, cursor ?? null, undefined, callDate)
        .then(next =>
          setCalls(prev =>
            cursor && prev ? { ...next, rows: [...prev.rows, ...next.rows] } : next,
          ),
        )
        .catch(e => setCallsError(message(e)))
        .finally(() => setCallsLoading(false));
    },
    [ctx, filter, range, callDate],
  );

  useEffect(() => {
    if (ctx && !ctx.capabilities.cap_review_queue && filter !== 'Messages') {
      setFilter('Messages');
    }
  }, [ctx, filter]);

  useEffect(() => {
    if (screen === 'calls') loadCalls();
  }, [screen, loadCalls]);

  // Calls normally arrive via Retell's webhook within seconds. The 15-minute
  // reconcile worker is only a repair path, so a viewer must not have to wait
  // for it or manually reload to see a call that just ended. Pause polling in a
  // background tab to avoid needless customer/API traffic.
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === 'visible') {
        loadSummary();
        if (screen === 'calls') loadCalls();
      }
    };
    const timer = window.setInterval(refresh, 10_000);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [loadSummary, loadCalls, screen]);

  // Detail
  useEffect(() => {
    if (screen !== 'detail' || !callId || !ctx) return;
    setDetailLoading(true);
    setDetailError(null);
    api
      .call(callId, ctx.branch.id)
      .then(setDetail)
      .catch(e => {
        setDetail(null);
        setDetailError(message(e));
      })
      .finally(() => setDetailLoading(false));
  }, [screen, callId, ctx, reloadDetail]);

  // A live call page stays useful instead of becoming a dead placeholder.
  // Poll only while Retell has not yet posted completion; once transcript and
  // recording arrive this stops automatically.
  useEffect(() => {
    if (screen !== 'detail' || !detail?.isLive) return;
    const timer = window.setInterval(() => setReloadDetail(n => n + 1), 4_000);
    return () => window.clearInterval(timer);
  }, [screen, detail?.isLive]);

  // Themes + sentiment
  useEffect(() => {
    if (screen !== 'themes' || !ctx) return;
    setThemesLoading(true);
    setThemesError(null);
    Promise.all([api.themes(ctx.branch.id, topic), api.sentiment(ctx.branch.id, 7)])
      .then(([t, s]) => {
        setThemes(t);
        setSentiment(s);
      })
      .catch(e => setThemesError(message(e)))
      .finally(() => setThemesLoading(false));
  }, [screen, topic, ctx, reloadThemes]);

  const openCall = (id: string) => {
    setCallId(id);
    setDetail(null);
    setScreen('detail');
  };

  const pickTopic = (name: string) => {
    setTopic(name);
    setScreen('themes');
  };

  /** One chart segment → a filtered day of calls → recording in the detail view. */
  const openSentimentCalls = (date: string, sentiment: 'positive' | 'neutral' | 'frustrated') => {
    setCallDate(date);
    setFilter(sentiment === 'positive' ? 'Positive' : sentiment === 'neutral' ? 'Neutral' : 'Frustrated');
    setCalls(null);
    setScreen('calls');
  };

  const changeBranch = (b: BranchSummary) => {
    setBranchId(b.id);
    setSummary(null);
    setCalls(null);
    setDetail(null);
    setThemes(null);
    setSentiment(null);
    setScreen('overview');
    setCallId(null);
  };

  const changeTenant = (nextTenantId: string) => {
    // Clear the old customer's state before loading the new one. A stale call
    // must never flash while an internal user moves between customers.
    setTenantId(nextTenantId);
    setBranchId(undefined);
    setSummary(null);
    setCalls(null);
    setDetail(null);
    setThemes(null);
    setSentiment(null);
    setScreen('overview');
    setCallId(null);
  };

  // Checked BEFORE ctxError: a refusal is an answer, not a fault, and must never
  // be shown as a broken API with a retry button that cannot succeed.
  if (accessDenied) {
    return <NoAccess denial={accessDenied.denial} fallbackMessage={accessDenied.message} />;
  }

  if (ctxError) {
    return (
      <div style={{ padding: 48, maxWidth: 720, margin: '0 auto' }}>
        <Failure
          message={`${ctxError} This looks like a connection problem rather than a permissions one. If it keeps happening, contact ModelProp support.`}
          onRetry={loadCtx}
          label="CANNOT REACH THE DASHBOARD API"
        />
      </div>
    );
  }

  if (!ctx) {
    return (
      <div style={{ padding: 48, maxWidth: 720, margin: '0 auto' }}>
        <LoadingPanel label="CONNECTING" />
      </div>
    );
  }

  // ModelProp staff must deliberately choose a customer. Customer users never
  // see this screen or the list behind it.
  if (ctx.actor?.internal && !tenantId) {
    return (
      <CustomerPicker
        tenants={ctx.actor.availableTenants}
        onSelect={changeTenant}
      />
    );
  }

  // The server owns the wording as well as the bounds. Until the first summary
  // lands, fall back to the option list rather than formatting a date here —
  // a label computed in the browser can disagree with the data behind it.
  const rangeOptions = ctx.ranges ?? [{ key: 'today' as RangeKey, label: 'Today', longLabel: 'Today' }];
  const rangeLabel =
    summary?.range?.longLabel
    ?? rangeOptions.find(r => r.key === range)?.longLabel
    ?? '';

  const changeRange = (next: RangeKey) => {
    setRange(next);
    setSummary(null);
    setCalls(null);
  };

  return (
    <div style={{ display: 'flex', height: '100vh', width: '100%', overflow: 'hidden', background: c.surface }}>
      <Rail
        ctx={ctx}
        screen={screen}
        callCount={summary?.metrics.find(m => m.key === 'calls_handled')?.value ?? null}
        followUpCount={summary?.queue.items.length ?? null}
        onNavigate={s => {
          setScreen(s);
          if (s !== 'detail') setCallId(null);
        }}
        onLocked={openUpgrade}
      />

      <main className="vp-main" style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Header
          ctx={ctx}
          ranges={rangeOptions}
          range={range}
          rangeLabel={rangeLabel}
          lastUpdated={lastUpdated}
          onRefresh={() => {
            loadSummary();
            if (screen === 'calls') loadCalls();
          }}
          onRangeChange={changeRange}
          onBranchChange={changeBranch}
          onTenantChange={changeTenant}
        />

        <div className="vp-scroll" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
          {screen === 'overview' ? (
            <Overview
              ctx={ctx}
              summary={summary}
              onOpenCall={openCall}
              onGoThemes={() => setScreen('themes')}
              onGoCalls={() => {
                // The Action Centre's Call Record is the complete archive for
                // every tier. Messages remain a filter inside it, not a
                // different destination that makes short calls disappear.
                setFilter('All');
                setCallDate(null);
                setCalls(null);
                setScreen('calls');
              }}
              onOpenSentimentCalls={openSentimentCalls}
              onPickTopic={pickTopic}
              onUpgrade={openUpgrade}
              onConnectCrm={() => setCrmActivationOpen(true)}
              onChangeRange={changeRange}
            />
          ) : null}

          {screen === 'calls' ? (
            <Calls
              ctx={ctx}
              calls={calls}
              onChangeRange={changeRange}
              filter={filter}
              loading={callsLoading}
              error={callsError}
              onFilter={f => {
                setFilter(f);
                setCallDate(null);
                setCalls(null);
              }}
              onOpenCall={openCall}
              onRetry={() => loadCalls()}
              onLoadMore={() => loadCalls(calls?.nextCursor ?? null)}
            />
          ) : null}

          {screen === 'detail' ? (
            <CallDetail
              ctx={ctx}
              detail={detail}
              loading={detailLoading}
              error={detailError}
              onBack={() => {
                setScreen('calls');
                setCallId(null);
              }}
              onRetry={() => setReloadDetail(n => n + 1)}
              onUpgrade={openUpgrade}
            />
          ) : null}

          {screen === 'studio' ? <VoiceStudio ctx={ctx} /> : null}

          {/*
            Team is owner/admin only. The rail already hides it, but a pasted
            #/team link would otherwise render an empty screen and fire requests
            the server refuses — so redirect rather than show a dead end.
          */}
          {screen === 'team' && ctx.actor?.canManageTeam ? <Team tenantName={ctx.tenant.name} /> : null}

          {screen === 'themes' ? (
            <Themes
              ctx={ctx}
              themes={themes}
              sentiment={sentiment}
              loading={themesLoading}
              error={themesError}
              onPickTopic={setTopic}
              onOpenCall={openCall}
              onOpenSentimentCalls={openSentimentCalls}
              onRetry={() => setReloadThemes(n => n + 1)}
              onUpgrade={openUpgrade}
            />
          ) : null}
        </div>
      </main>

      <nav className="vp-mobile-nav" aria-label="Dashboard navigation">
        {([
          ['overview', 'Home'],
          ['calls', 'Calls'],
          ['studio', 'Studio'],
          ['themes', 'Insights'],
        ] as const).map(([target, label]) => (
          <button
            key={target}
            className="vp-reset"
            aria-current={screen === target ? 'page' : undefined}
            onClick={() => { setScreen(target); setCallId(null); }}
          >
            {label}
          </button>
        ))}
      </nav>

      {ctx.capabilities.cap_review_queue ? <AskVoiceProp ctx={ctx} /> : null}
      {upgrade ? <UpgradeDrawer tier={upgrade} focus={upgradeFocus} onClose={closeUpgrade} /> : null}
      {crmActivationOpen ? <CrmActivationModal branchId={ctx.branch.id} onClose={() => setCrmActivationOpen(false)} /> : null}
    </div>
  );
}
